// Layout · Grid
//
// CSS-grid container. Closed-enum `gap` (shared with Stack/Inline) and
// `columns` (numeric or named asymmetric template). No `style`.

import type { ReactNode } from "react";
import { GAP_TO_SPACE, type Gap } from "@ds/layout/Stack.helpers";
import { LAYOUT } from "@ds/tokens/palette";

export type { Gap };

export type GridAlign   = "start" | "center" | "end" | "stretch";
export type GridJustify = "start" | "center" | "end" | "stretch";

/**
 * Column track definition. Closed shape:
 *   - `number`             → `repeat(N, minmax(0, 1fr))`
 *   - `"auto"`             → `repeat(auto-fill, minmax(220px, 1fr))`
 *   - `"auto-wide"`        → `repeat(auto-fill, minmax(280px, 1fr))` — same
 *                            idea with a track wide enough for a label/value
 *                            pair; two columns on a laptop, three or more on
 *                            a wide screen, without a media query.
 *   - `"sidebar-content"`  → `"200px 1fr"`
 *   - `"content-sidebar"`  → `"1fr 200px"`
 *   - `"label-value"`      → `"max-content 1fr"`
 *   - `"label-value-tight"`→ `"max-content max-content"` — both tracks hug their
 *                            content, so the pair can be flushed to one edge
 *                            (`justify="end"`) the way a document prints a
 *                            date/terms block.
 *   - `"main-side"`        → `"2fr 1fr"` — 2/3 main + 1/3 side (6 entity configs)
 *   - `"main-sidebar"`     → `"minmax(0,1fr) <sidebarWidth>px"` — fluid main + fixed sidebar (4 entity configs)
 */
export type GridColumns =
  | number
  | "auto"
  | "auto-wide"
  | "sidebar-content"
  | "content-sidebar"
  | "label-value"
  | "label-value-tight"
  | "main-side"
  | "main-sidebar";

export interface GridInput {
  children: ReactNode;
  /** Column track definition. Default `2`. */
  columns?: GridColumns;
  /** Uniform gap. Default `"md"`. */
  gap?: Gap;
  rowGap?: Gap;
  columnGap?: Gap;
  align?: GridAlign;
  /** How each item sits INSIDE its track (`justify-items`). */
  justify?: GridJustify;
  /**
   * Where the track group sits inside the container (`justify-content`) — only
   * meaningful when the tracks don't fill the width (e.g. `label-value-tight`).
   */
  justifyTracks?: GridJustify;
  fullWidth?: boolean;
  testId?: string;
  className?: string;
}

export type GridProps = GridInput;

function alignToCss(a: GridAlign): string {
  if (a === "start") return "flex-start";
  if (a === "end")   return "flex-end";
  return a;
}

function justifyToCss(j: GridJustify): string {
  if (j === "start") return "flex-start";
  if (j === "end")   return "flex-end";
  return j;
}

function resolveColumns(columns: GridColumns): string {
  if (columns === "auto")             return "repeat(auto-fill, minmax(220px, 1fr))";
  if (columns === "auto-wide")        return "repeat(auto-fill, minmax(280px, 1fr))";
  if (columns === "sidebar-content")  return "200px 1fr";
  if (columns === "content-sidebar")  return "1fr 200px";
  if (columns === "label-value")      return "max-content 1fr";
  if (columns === "label-value-tight") return "max-content max-content";
  if (columns === "main-side")        return "2fr 1fr";
  if (columns === "main-sidebar")     return `minmax(0,1fr) ${LAYOUT.sidebarWidth}px`;
  return `repeat(${columns}, minmax(0, 1fr))`;
}

export function Grid({
  children,
  columns = 2,
  gap = "md",
  rowGap,
  columnGap,
  align,
  justify,
  justifyTracks,
  fullWidth,
  testId,
  className,
}: GridProps) {
  const style: Record<string, unknown> = {
    display: "grid",
    gridTemplateColumns: resolveColumns(columns),
    minWidth: 0,
  };
  if (rowGap !== undefined || columnGap !== undefined) {
    style.rowGap    = GAP_TO_SPACE[rowGap    ?? gap];
    style.columnGap = GAP_TO_SPACE[columnGap ?? gap];
  } else {
    style.gap = GAP_TO_SPACE[gap];
  }
  if (align)     style.alignItems   = alignToCss(align);
  if (justify)   style.justifyItems = justifyToCss(justify);
  // `justify-content` in grid takes the CSS-grid keywords verbatim (`end`,
  // not `flex-end`) — no flexbox translation here.
  if (justifyTracks) style.justifyContent = justifyTracks;
  if (fullWidth) style.width        = "100%";

  return (
    <div
      data-testid={testId}
      className={className}
      style={style}
    >
      {children}
    </div>
  );
}
